import React from 'react'
import { Button, Divider, Select, Space, Tooltip, Typography, Image } from 'antd'
import { IconFont } from '@/components'
import { FilterDropdownProps } from 'antd/lib/table/interface'
import { formatDateTime } from '@/utils/date'
import { riskLevelMap, interceptStatusMap, resourceLevelMap, logAlarmMap } from './constants'
import { t } from '@/i18n'

const { Text } = Typography

const FilterIcon = ({ filtered }: { filtered?: boolean }) => (
  <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
    <IconFont
      type="icon-a-shaixuan1"
      style={{
        fontSize: '14px',
        color: filtered ? '#4238C9' : '#999',
        cursor: 'pointer',
        transition: 'color 0.3s',
      }}
    />
  </Tooltip>
)

export const createTableColumns = (
  taskInfo: any,
  attackType: any[],
  getDrawerData: (id: string) => void,
  setActive: (id: string) => void,
  riskLevelSelectOpen: boolean,
  setRiskLevelSelectOpen: (open: boolean) => void,
  attackTypeSelectOpen: boolean,
  setAttackTypeSelectOpen: (open: boolean) => void,
  resourceLevelSelectOpen: boolean,
  setResourceLevelSelectOpen: (open: boolean) => void,
  interceptStatusSelectOpen: boolean,
  setInterceptStatusSelectOpen: (open: boolean) => void,
  deviceStatusSelectOpen: boolean,
  setDeviceStatusSelectOpen: (open: boolean) => void,
  filterParams: any,
) => {
  const baseColumns = [
    {
      title: t('用例名称'),
      dataIndex: 'case_name',
      key: 'case_name',
      width: '30%',
      render: (_: any, record: any) => (
        <Text
          ellipsis={{
            tooltip: {
              getPopupContainer: () => document.body,
            },
          }}
          className="case_name"
          style={{ width: 250, cursor: 'pointer' }}
          onClick={() => {
            getDrawerData(record.id)
          }}
        >
          {record?.case_name}
        </Text>
      ),
    },
    {
      title: t('危险等级'),
      dataIndex: 'risk_level',
      key: 'risk_level',
      width: '10%',
      filteredValue: filterParams.risk_level,
      render: (_: any, record: any) => (
        <div>
          <Image src={riskLevelMap[record?.risk_level]?.iconSrc} width={19} />
          <span className={riskLevelMap[record?.risk_level]?.class}>
            {riskLevelMap[record?.risk_level]?.label}
          </span>
        </div>
      ),

      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择危险等级')}
            value={props.selectedKeys as string[]}
            open={riskLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setRiskLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setRiskLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('严重'), value: 'RiskLevelGross' },
              { label: t('高危'), value: 'RiskLevelHigh' },
              { label: t('中危'), value: 'RiskLevelMiddle' },
              { label: t('低危'), value: 'RiskLevelLow' },
            ]}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
    },
    {
      title: t('攻击类型'),
      dataIndex: 'attack_type_names',
      key: 'attack_type_names',
      width: '10%',
      filteredValue: filterParams.attack_type_names,
      render: (_: any, record: any) => (
        <Text
          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
          style={{ width: 100 }}
        >
          {record?.attack_type_name}
        </Text>
      ),

      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            mode="multiple"
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择攻击类型')}
            value={props.selectedKeys as string[]}
            open={attackTypeSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setAttackTypeSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setAttackTypeSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={attackType.map(item => ({ label: item, value: item }))}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
    },
    {
      title: t('用例分级'),
      dataIndex: 'resource_level',
      key: 'resource_level',
      width: '7%',
      filteredValue: filterParams.resource_level,
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择用例分级')}
            value={props.selectedKeys as string[]}
            open={resourceLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setResourceLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setResourceLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('初级'), value: 'ResourceLevelJunior' },
              { label: t('中级'), value: 'ResourceLevelMiddle' },
              { label: t('高级'), value: 'ResourceLevelSenior' },
            ]}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
      render: (_, record: any) => (
        <div>
          <span>{resourceLevelMap[record?.resource_level]?.label || '-'}</span>
        </div>
      ),
    },
    {
      title: t('验证结果'),
      dataIndex: 'intercept_status',
      key: 'intercept_status',
      width: '10%',
      filteredValue: filterParams.intercept_status,
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择验证结果')}
            value={props.selectedKeys as string[]}
            open={interceptStatusSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setInterceptStatusSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setInterceptStatusSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('拦截成功'), value: 'intercept_status_intercept_success' },
              { label: t('防护失效'), value: 'intercept_status_intercept_defence_fail' },
              { label: t('检测成功'), value: 'intercept_status_detect_success' },
            ]}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
      render: (_: any, record: any) => (
        <div>
          <span className={interceptStatusMap[record?.intercept_status]?.class}>
            {interceptStatusMap[record?.intercept_status]?.label}
          </span>
        </div>
      ),
    },
    {
      title: t('告警结果'),
      dataIndex: 'log_alarm',
      key: 'log_alarm',
      width: '10%',
      render: (_, record: any) => (
        <div>
          <span className={logAlarmMap[record?.has_alert]?.class}>
            {logAlarmMap[record?.has_alert]?.label || '-'}
          </span>
        </div>
      ),
    },
    {
      title: t('执行时间'),
      dataIndex: 'sort_field_exec_time',
      key: 'sort_field_exec_time',
      width: '14%',
      sorter: (a: any, b: any) => a.exec_time - b.exec_time,
      render: (_: any, record: any) => (
        <Tooltip
          title={record.exec_time ? record.exec_time : '-'}
          getPopupContainer={() => document.body}
        >
          {record.exec_time ? formatDateTime(record.exec_time, 'mdhm') : '-'}
        </Tooltip>
      ),
    },
    {
      title: t('操作'),
      fixed: 'right',
      width: '10%',
      render: (_: any, record: any) => (
        <div
          className="operation"
          onClick={() => {
            getDrawerData(record.id)
            setActive(record.id)
          }}
        >
          <IconFont type="icon-chakan" />
          <span>{t('查看')}</span>
        </div>
      ),
    },
  ]

  // 如果有设备信息，添加设备结果分布列
  if (taskInfo?.devices?.length > 0) {
    const deviceColumn = {
      title: t('设备结果分布'),
      dataIndex: 'device_status_list',
      key: 'device_status_list',
      width: '12%',
      filteredValue: filterParams.device_status_list,
      filterIcon: (filtered: boolean) => <FilterIcon filtered={filtered} />,
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择设备结果')}
            value={props.selectedKeys as string[]}
            open={deviceStatusSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setDeviceStatusSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setDeviceStatusSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { value: 'ResourceDeviceStatusDetectSuccess', label: t('检测成功') },
              { value: 'ResourceDeviceStatusInterceptSuccess', label: t('拦截成功') },
              { value: 'ResourceDeviceStatusProtectionInvalid', label: t('防护失效') },
            ]}
          />
        </div>
      ),

      render: (_: any, record: any) => {
        const deviceStatusList: { name: string; count: number }[] = []
        const statusOrder = [
          { status: 'ResourceDeviceStatusDetectSuccess', name: t('检测成功') },
          { status: 'ResourceDeviceStatusInterceptSuccess', name: t('拦截成功') },
          { status: 'ResourceDeviceStatusProtectionInvalid', name: t('防护失效') },
          { status: 'ResourceDeviceStatusNotInvolved', name: t('不涉及') },
        ]

        statusOrder.forEach(orderItem => {
          const foundItem = record?.device_results?.find(
            (item: any) => item.device_status === orderItem.status,
          )
          deviceStatusList.push({
            name: orderItem.name,
            count: foundItem ? foundItem.count : 0,
          })
        })

        return (
          <div>
            {record?.device_results?.length === 1 && record.device_results[0]?.count === 1
              ? statusOrder.find(item => item.status === record?.device_results[0]?.device_status)
                  ?.name
              : record?.device_results?.length > 0
              ? deviceStatusList.map((item, index) => {
                  if (item.name === '检测成功') {
                    return (
                      <React.Fragment key={index}>
                        <Tooltip title={`${t('检测成功')}${item.count}`}>
                          <span style={{ color: '#72B851' }}>{item.count}</span>
                        </Tooltip>
                        <span style={{ margin: '0 4px' }}>|</span>
                      </React.Fragment>
                    )
                  } else if (item.name === '拦截成功') {
                    return (
                      <React.Fragment key={index}>
                        <Tooltip title={`${t('拦截成功')}${item.count}`}>
                          <span style={{ color: '#5677D9' }}>{item.count}</span>
                        </Tooltip>
                        <span style={{ margin: '0 4px' }}>|</span>
                      </React.Fragment>
                    )
                  } else if (item.name === '防护失效') {
                    return (
                      <React.Fragment key={index}>
                        <Tooltip title={`${t('防护失效')}${item.count}`}>
                          <span style={{ color: '#F44F4F' }}>{item.count}</span>
                        </Tooltip>
                        <span style={{ margin: '0 4px' }}>|</span>
                      </React.Fragment>
                    )
                  } else {
                    return (
                      <React.Fragment key={index}>
                        <Tooltip title={`${t('不涉及')}${item.count}`}>
                          <span style={{ color: '#333333' }}>{item.count}</span>
                        </Tooltip>
                      </React.Fragment>
                    )
                  }
                })
              : '-'}
          </div>
        )
      },
    }

    // 在告警结果列之前插入设备结果分布列
    const insertIndex = baseColumns.findIndex(col => col.key === 'log_alarm')
    baseColumns.splice(insertIndex, 0, deviceColumn)
  }

  return baseColumns
}
